import React, { useEffect, useMemo, useState } from 'react'
import { Card, Checkbox } from '@sede-enterprise/shell-ds-react-framework';
import DetailsTopContributors from '../../components/DetailsTopContributors/DetailsTopContributors';
import { generateCheckboxConfig } from '../../util/checkboxConfig';
import { INTERVAL_OPTIONS } from '../../util/constants';
import { calculateFixedYRange, generateRequests, getSiteType } from '../../util/helperFunctions';
import useUpdateChart from '../../util/hooks/useUpdateChart';
import MetricChart from '../../components/MetricChart/MetricChart';


const CMLDetailsChart = (props) => {
    const {
        units,
        prodUnit,
        cmlID,
        site,
        metricData,
        cmlData,
        loading,
        start,
        setStart,
        end,
        setEnd,
        interval,
        setSelectedInterval,
        setRequestParams,
        formDirty,
        refreshChartData
    } = props;

    const { isNorco, isNorcoWest, isRhienland, isSyngas, isBonga } = getSiteType(site, prodUnit);
    const [cmlCheckboxes, setCmlCheckboxes] = useState(
        generateCheckboxConfig({ units, site, prodUnit, isNorco, isNorcoWest, isRhienland, isSyngas, isBonga })
    );

    useEffect(() => {
        const updatedCheckboxConfig = generateCheckboxConfig({ units, site, prodUnit, isNorco, isNorcoWest, isRhienland, isSyngas, isBonga });
        setCmlCheckboxes((prevCheckboxes) => {
            const mergedCheckboxes = Object.keys(updatedCheckboxConfig).reduce((acc, key) => {
                acc[key] = {
                    ...updatedCheckboxConfig[key],
                    enabled: prevCheckboxes[key]?.enabled ?? updatedCheckboxConfig[key].enabled, // Preserve 'enabled' if it exists
                };
                return acc;
            }, {});
            return {
                ...mergedCheckboxes,
                "MissingFeatures": {
                    ...mergedCheckboxes.MissingFeatures,
                    available: false,
                },
                "ForecastWallThickness": {
                    ...mergedCheckboxes.ForecastWallThickness,
                    available: false,
                },
            };
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [units, site, prodUnit, isNorco, isNorcoWest, isRhienland, isSyngas, isBonga])

    const cmlRequests = useMemo(() => {
        return generateRequests(cmlCheckboxes, cmlID, "CMLExpression");
    }, [cmlCheckboxes, cmlID]);

    useEffect(() => {
        if (
            !!cmlRequests.length
        ) {
            refreshChartData([
                ...cmlRequests,
            ]);
        } else {
            refreshChartData([]);
        }
    }, [
        cmlRequests,
        refreshChartData
    ]);

    const cmlCheckboxClicked = (id) => {
        if (id === "PredictedCorrosionRateConfidenceBandLowerValue") {
            setCmlCheckboxes({
                ...cmlCheckboxes,
                "PredictedCorrosionRateConfidenceBandUpperValue": {
                    ...cmlCheckboxes.PredictedCorrosionRateConfidenceBandUpperValue,
                    enabled: !cmlCheckboxes.PredictedCorrosionRateConfidenceBandUpperValue.enabled,
                },
                "PredictedCorrosionRateConfidenceBandLowerValue": {
                    ...cmlCheckboxes.PredictedCorrosionRateConfidenceBandLowerValue,
                    enabled: !cmlCheckboxes.PredictedCorrosionRateConfidenceBandLowerValue.enabled,
                }
            });
        }
        else {
            setCmlCheckboxes({
                ...cmlCheckboxes,
                [id]: {
                    ...cmlCheckboxes[id],
                    enabled: !cmlCheckboxes[id].enabled,
                },
            });
        }
    };

    const updateChart = useUpdateChart({
        setRequestParams,
        start,
        end,
        interval,
    });


    const fixedYRange = () => {
        const fixedYRangeVariables = [
            'Measured wall thickness',
            'Predicted wall thickness',
            'Measured corrosion rate',
            'Predicted corrosion rate',
            'Predicted corrosion rate confidence band',
        ];
        return calculateFixedYRange(metricData, units, fixedYRangeVariables);
    };
    return (
        <div className='features-container'>

            <Card className='details-chart-card' bodyPadding={false}>
                <div className='graphs-container'>
                    <MetricChart
                        yaxes={{
                            yaxis: { fixedrange: false, side: "left" },
                            yaxis2: {
                                fixedrange: false,
                                side: "right",
                                overlaying: "y",
                                anchor: "x",
                            },
                        }}
                        data={metricData}
                        sensorData={cmlData}
                        metricData={metricData}
                        loading={loading}
                        start={start}
                        setStart={setStart}
                        end={end}
                        setEnd={setEnd}
                        interval={interval}
                        fixedYRange={fixedYRange()}
                        setSelectedInterval={setSelectedInterval}
                        intervalOpts={INTERVAL_OPTIONS}
                        formDirty={formDirty}
                        onSubmit={updateChart}
                    />
                </div>
                <div className='metric-chart-checkboxes'>
                    <div className='metric-checkboxes-row'>
                        <p className='data-label'>Features</p>
                        <div className='checkboxes-container'>
                            {Object.entries(cmlCheckboxes)
                                .filter(([key, value]) => value.group === 1 && value.available)
                                .map(k => k[0])
                                .map((key) => {
                                    return (
                                        <Checkbox
                                            className='checkbox-item'
                                            key={key}
                                            label={cmlCheckboxes[key].name + " " + cmlCheckboxes[key].unit}
                                            checked={cmlCheckboxes[key].enabled}
                                            onChange={() => cmlCheckboxClicked(key)}
                                        />
                                    );
                                })}
                        </div>
                    </div>
                    <div className='metric-checkboxes-row'>
                        <p className='data-label'> Features (Second Y-axis)</p>
                        <div className='checkboxes-container'>
                            {Object.entries(cmlCheckboxes)
                                .filter(([key, value]) => value.group === 2 && value.available)
                                .map(k => k[0])
                                .map(
                                    (key) => {
                                        return (
                                            <Checkbox
                                                className='checkbox-item'
                                                key={key}
                                                label={
                                                    cmlCheckboxes[key].name + " " + cmlCheckboxes[key].unit
                                                }
                                                checked={
                                                    cmlCheckboxes[key].enabled
                                                }
                                                onChange={() =>
                                                    cmlCheckboxClicked(key)
                                                }
                                            />
                                        );
                                    })}
                        </div>
                    </div>
                    <div className='metric-checkboxes-row'>
                        <p className='data-label'>Predictions</p>
                        <div className='checkboxes-container'>
                            {Object.entries(cmlCheckboxes)
                                .filter(([key, value]) => value.group === 3 && value.available)
                                .map(k => k[0])
                                .map((key) => {
                                    return (
                                        <Checkbox
                                            className='checkbox-item'
                                            key={key}
                                            label={
                                                cmlCheckboxes[key].name.replace(
                                                    "Predicted corrosion rate confidence band lower value",
                                                    "Predicted corrosion rate confidence band"
                                                ) +
                                                " " +
                                                cmlCheckboxes[key].unit
                                            }
                                            checked={cmlCheckboxes[key].enabled}
                                            onChange={() =>
                                                cmlCheckboxClicked(key)
                                            }
                                        />
                                    );
                                })}
                        </div>
                    </div>
                </div>
            </Card>
            <br />
            <br />
            <DetailsTopContributors
                units={units}
                pathPrefix="CML"
                corrosionLocationID={cmlID}
                start={start}
                end={end}
                isRealSensor={cmlData?.isRealSensor}
            />
        </div>
    )
}

export default CMLDetailsChart